import { useState, useEffect, useCallback } from "react";
import { onSnapshot, query, where, orderBy, Timestamp } from "firebase/firestore";
import { NotificationsAdminRef } from "../firebase";
import { useAuth } from "./useAuth";

export const useNotificationBadge = () => {
  const [notificationCount, setNotificationCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { currentUser, organizationId } = useAuth();

  // Track when notifications were last viewed in this session
  const [lastViewedTime, setLastViewedTime] = useState(() => {
    const stored = sessionStorage.getItem('notificationsLastViewedTime');
    return stored ? new Date(stored) : null;
  });

  useEffect(() => {
    // Reset viewed time on new login/user change
    if (currentUser) {
      setLastViewedTime(null);
      sessionStorage.removeItem('notificationsLastViewedTime');
    }
  }, [currentUser?.id]);

  useEffect(() => {
    if (!currentUser?.lastLoginDate || !organizationId) {
      setNotificationCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Determine the cutoff time for counting notifications
    // Use the later of: last login time or last viewed time in this session
    let cutoffTime = currentUser.lastLoginDate;
    if (lastViewedTime && lastViewedTime > currentUser.lastLoginDate.toDate()) {
      cutoffTime = Timestamp.fromDate(lastViewedTime);
    }

    console.log("Notification badge cutoff time:", cutoffTime);

    // Create Firestore listener for real-time updates
    const unsubscribe = onSnapshot(
      query(
        NotificationsAdminRef,
        where("organization", "==", organizationId),
        where("createdAt", ">", cutoffTime),
        orderBy("createdAt", "desc")
      ),
      (snapshot) => {
        const count = snapshot.docs.length;
        console.log("Notification badge count updated:", count);
        setNotificationCount(count);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error listening to notifications:", error);
        setIsLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => {
      unsubscribe();
    };
  }, [currentUser?.lastLoginDate, organizationId, lastViewedTime]);

  // Function to mark notifications as viewed (clear badge for session)
  const markNotificationsAsViewed = useCallback(() => {
    const now = new Date();
    console.log("Marking notifications as viewed at:", now);
    console.log("Current lastViewedTime before update:", lastViewedTime);
    setLastViewedTime(now);
    sessionStorage.setItem('notificationsLastViewedTime', now.toISOString());
    console.log("Set new lastViewedTime:", now);
  }, [lastViewedTime]);

  // Calculate display count - show count of notifications that arrived after last viewing
  // If no lastViewedTime is set, show all notifications since login
  // If lastViewedTime is set, the query already filters to only show newer notifications
  const displayCount = notificationCount;

  return {
    notificationCount: displayCount,
    isLoading,
    markNotificationsAsViewed,
    hasNewNotifications: displayCount > 0
  };
};
